// h-domain: Bausteine für eigene Domains – Status-Etikett, DNS-Anleitung und
// die schlichte Liste für die Betreiber-Ansicht eines Radios.

import { useEffect, useState } from "react";
import { domainApi } from "../api";
import type { DomainListe, DomainStatus, RadioDomain } from "../types";
import { Badge, Card } from "./ui";
import { Sym } from "./social-ui";
import { datumText } from "./hosting-ui";

export const DOMAIN_STATUS: Record<DomainStatus, { label: string; tone: "warning" | "info" | "success" }> = {
  wartet: { label: "Zeigt noch nicht auf uns", tone: "warning" },
  zeigt_auf_uns: { label: "Zeigt auf uns", tone: "info" },
  zertifikat_aktiv: { label: "Zertifikat aktiv", tone: "success" },
};

export function DomainStatusBadge({ status }: { status: DomainStatus }): JSX.Element {
  const s = DOMAIN_STATUS[status];
  return <Badge tone={s.tone} dot>{s.label}</Badge>;
}

/** Endungen, bei denen die Hauptdomain drei Teile hat (beispiel.co.uk). */
const ZWEITEILIGE_ENDUNGEN = new Set(["co.uk", "org.uk", "co.at", "or.at", "com.au", "co.nz", "com.br", "co.jp"]);

/**
 * Hauptdomain (beispiel.de) oder Unterdomain (radio.beispiel.de)? An der
 * Hauptdomain geht bei den meisten Anbietern kein CNAME – dort braucht es A/AAAA.
 */
export function istHauptdomain(host: string): boolean {
  const teile = host.split(".");
  const endung = teile.slice(-2).join(".");
  return teile.length <= (ZWEITEILIGE_ENDUNGEN.has(endung) ? 3 : 2);
}

/** Der Name, wie ihn DNS-Oberflächen erwarten: „@“ für die Hauptdomain, sonst der vordere Teil. */
export function dnsName(host: string): string {
  if (istHauptdomain(host)) return "@";
  const teile = host.split(".");
  const endung = teile.slice(-2).join(".");
  return teile.slice(0, teile.length - (ZWEITEILIGE_ENDUNGEN.has(endung) ? 3 : 2)).join(".");
}

export interface DnsZeile { typ: "CNAME" | "A" | "AAAA"; name: string; wert: string; fuer: string }

/** Die Einträge, die beim Domain-Anbieter anzulegen sind – konkrete Werte. */
export function dnsEintraege(d: Pick<RadioDomain, "host" | "www_host">, ziel: DomainListe["ziel"]): DnsZeile[] {
  const aus: DnsZeile[] = [];
  const v4 = ziel.ips.filter((ip) => !ip.includes(":"));
  const v6 = ziel.ips.filter((ip) => ip.includes(":"));
  const fuer = (host: string): void => {
    if (!istHauptdomain(host) && ziel.cname) {
      aus.push({ typ: "CNAME", name: dnsName(host), wert: ziel.cname, fuer: host });
      return;
    }
    for (const ip of v4) aus.push({ typ: "A", name: dnsName(host), wert: ip, fuer: host });
    for (const ip of v6) aus.push({ typ: "AAAA", name: dnsName(host), wert: ip, fuer: host });
    if (v4.length === 0 && v6.length === 0 && ziel.cname) aus.push({ typ: "CNAME", name: dnsName(host), wert: ziel.cname, fuer: host });
  };
  fuer(d.host);
  if (d.www_host) fuer(d.www_host);
  return aus;
}

/** Betreiber-Ansicht: welche Domains ein Radio hat und wie weit sie sind. Nur lesen. */
export function DomainKarteBetrieb({ tenantId }: { tenantId: string }): JSX.Element {
  const [domains, setDomains] = useState<RadioDomain[] | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    domainApi.vonRadio(tenantId).then(setDomains).catch(() => setFehler(true));
  }, [tenantId]);

  return (
    <Card
      title={<span className="inline-flex items-center gap-2"><Sym name="link" className="h-4 w-4 text-muted" />Eigene Domains</span>}
      description="Pflegt das Radio selbst unter Aussehen › Eigene Adresse."
      className="mb-5"
      padded={false}
    >
      {fehler && <p className="px-5 py-4 text-sm text-muted">Die Domains ließen sich nicht laden.</p>}
      {!fehler && domains === null && <p className="px-5 py-4 text-sm text-muted">Lädt …</p>}
      {domains && domains.length === 0 && <p className="px-5 py-4 text-sm text-muted">Keine eigene Domain eingetragen.</p>}
      {domains && domains.length > 0 && (
        <ul className="divide-y divide-default">
          {domains.map((d) => (
            <li key={d.host} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-all font-mono text-sm text-primary">{d.host}</span>
                  {d.primaer && <Badge tone="brand">Hauptadresse</Badge>}
                </div>
                <div className="mt-0.5 text-xs text-muted">
                  {d.www_host ? `mit ${d.www_host} · ` : ""}
                  {d.geprueft_at ? `zuletzt geprüft ${datumText(d.geprueft_at, true)}` : "noch nie geprüft"}
                </div>
              </div>
              <DomainStatusBadge status={d.status} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
